import React, { useState } from "react";
import "./EventDetails.css";
import { Link } from "react-router-dom";
import ShareButton from "../sharefeature/ShareButton";
import clockicon from "../../Assets/clock-icon.svg";
import dateicon from "../../Assets/date-icon.svg";
import locationicon from "../../Assets/location-icon.svg";
import timeicon from "../../Assets/time-icon.svg";
import moreinfo from "../../Assets/more_infoicon.svg";
import { useParams } from "react-router-dom";
// Firestore imports removed
import { useEffect } from "react";
import { ApiService } from "../../services/api_service";
import { formatEventDateOnly } from "../../utils/date_formatter";
import { convertTo12HourFormat } from "../../utils/time_formatter";
import TermsAndConditions from "./components/TermsAndConditions";
import AppDownloadModal from "./components/AppDownloadModal";
import ArtistLineup from "./components/ArtistLineup";
import ArrowIcon from "../../Assets/getdirection_arrow.svg";
import Reveal from "../../common_components/Reveal";

const EventDetails = () => {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [similarEvents, setSimilarEvents] = useState([]);
  const [loadingSimilarEvents, setLoadingSimilarEvents] = useState(false);
  const [isTermsExpanded, setIsTermsExpanded] = useState(false);
  const [isFAQExpanded, setIsFAQExpanded] = useState(false);
  const [minimumTicketPrice, setMinimumTicketPrice] = useState(null);

  useEffect(() => {
    async function fetchEvent() {
      try {
        const response = await ApiService.getSingleEvent(eventId);
        const eventData = response.data || response;

        if (eventData) {
          setEvent(eventData);
        } else {
          setError("Event not found");
        }
      } catch (err) {
        console.error("Error fetching event:", err);
        setError("Failed to load event");
      } finally {
        setLoading(false);
      }
    }

    fetchEvent();
  }, [eventId]);

  // Fetch similar events
  useEffect(() => {
    async function fetchSimilarEvents() {
      if (!event) return;

      setLoadingSimilarEvents(true);
      try {
        const response = await ApiService.getAllEvents();
        let fetchedEvents = response.data || response;

        if (fetchedEvents && !Array.isArray(fetchedEvents) && typeof fetchedEvents === 'object') {
          fetchedEvents = Object.values(fetchedEvents);
        }

        if (!Array.isArray(fetchedEvents)) {
          fetchedEvents = [];
        }

        const venueText = (e) =>
          e?.eventLocation?.locationText ?? e?.eventLocationText ?? "";

        const events = fetchedEvents.filter((doc) => {
          const isSameLocation = venueText(doc) && venueText(doc) === venueText(event);
          const showInApp = doc.showInApp === true || doc.showInApp === null || doc.showInApp === undefined;
          const isNotCurrent = doc.id !== eventId;
          return isSameLocation && showInApp && isNotCurrent;
        }).slice(0, 5);

        setSimilarEvents(events);
      } catch (error) {
        console.error("Error fetching similar events:", error);
      } finally {
        setLoadingSimilarEvents(false);
      }
    }

    fetchSimilarEvents();
  }, [event, eventId]);

  useEffect(() => {
    async function fetchMinimumTicketPrice() {
      try {
        const datesRes = await ApiService.getEventDates(eventId);
        const datesList = datesRes.data || datesRes || [];

        let minPrice = null;

        for (const dateObj of datesList) {
          const slotsRes = await ApiService.getEventSlots(eventId, dateObj.id);
          const slotsList = slotsRes.data || slotsRes || [];

          for (const slotObj of slotsList) {
            const ticketsRes = await ApiService.getEventTickets(
              eventId,
              dateObj.id,
              slotObj.id
            );
            const ticketsList = ticketsRes.data || ticketsRes || [];

            ticketsList.forEach((ticket) => {
              const price = Number(ticket?.price);
              if (!Number.isNaN(price)) {
                minPrice = minPrice === null ? price : Math.min(minPrice, price);
              }
            });
          }
        }

        setMinimumTicketPrice(minPrice);
      } catch (err) {
        console.error("Error fetching minimum ticket price:", err);
        setMinimumTicketPrice(null);
      }
    }

    fetchMinimumTicketPrice();
  }, [eventId]);

  // FAQ data
  const faqData = [
    {
      question: "What is the refund policy?",
      answer: "Refunds are available up to 24 hours before the event start time. After that, no refunds will be processed."
    },
    {
      question: "Can I transfer my ticket to someone else?",
      answer: "Yes, you can transfer your ticket to someone else by updating the attendee details in your booking."
    },
    {
      question: "What should I bring to the event?",
      answer: "Please bring a valid ID proof and your digital ticket. No outside food or drinks are allowed."
    },
    {
      question: "Is parking available at the venue?",
      answer: "Yes, parking is available at the venue. Please arrive early as parking spaces are limited."
    },
    {
      question: "What is the age restriction for this event?",
      answer: `This event is restricted to ages ${event?.ageRestrictions || 18} and above. Valid ID proof is required for entry.`
    }
  ];

  function getMinimumTicketPrice() {
    return minimumTicketPrice;
  }

  function calculateEventDuration(startTime, endTime) {
    if (!startTime || !endTime) {
      return "Duration not available";
    }

    try {
      const start = startTime.toDate ? startTime.toDate() : new Date(startTime);
      const end = endTime.toDate ? endTime.toDate() : new Date(endTime);

      const diffMs = end.getTime() - start.getTime();

      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

      if (diffHours === 0) {
        return `${diffMinutes} ${diffMinutes === 1 ? 'Minute' : 'Minutes'}`;
      } else if (diffMinutes === 0) {
        return `${diffHours} ${diffHours === 1 ? 'Hour' : 'Hours'}`;
      } else {
        return `${diffHours} ${diffHours === 1 ? 'Hour' : 'Hours'} ${diffMinutes} ${diffMinutes === 1 ? 'Minute' : 'Minutes'}`;
      }
    } catch (error) {
      console.error("Error calculating duration:", error);
      return "Duration not available";
    }
  }

  // Convert Google Maps embed URL to directions URL
  function getDirectionsUrl(embedUrl, locationText) {
    if (!embedUrl && !locationText) {
      return "https://www.google.com/maps";
    }

    // If we have location text, use it to create a search/directions URL
    if (locationText) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locationText)}`;
    }

    // Try to extract place info from embed URL if possible
    // Embed URLs are like: https://www.google.com/maps/embed?pb=...
    // We'll use a fallback to search by location text
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locationText || '')}`;
  }

  function formatINR(amount) {
    if (amount === null || amount === undefined || isNaN(Number(amount))) return '';
    try {
      return Number(amount).toLocaleString('en-IN');
    } catch (_) {
      return String(amount);
    }
  }

  if (loading) return (
    <div className="event-detail">
      <div className="loading-container">
        <div className="loading-hero">
          <div className="loading-image"></div>
          <div className="loading-info">
            <div className="loading-title"></div>
            <div className="loading-details">
              <div className="loading-detail"></div>
              <div className="loading-detail"></div>
              <div className="loading-detail"></div>
              <div className="loading-detail"></div>
            </div>
            <div className="loading-price"></div>
          </div>
        </div>
        <div className="loading-content">
          <div className="loading-section"></div>
          <div className="loading-section"></div>
          <div className="loading-section"></div>
        </div>
      </div>
    </div>
  );

  if (error) return (
    <div className="event-detail">
      <div style={{ textAlign: 'center', padding: '50px', color: '#fff' }}>
        <p>Error: {error}</p>
      </div>
    </div>
  );

  const venueText = event.eventLocation?.venueName || event.eventLocation?.city || "To Be Announced";

  const venueTitle =
    event.eventLocation?.venueName ||
    event.eventLocation?.city ||
    event.eventLocation?.locationText ||
    "Venue";
  const venueDescription =
    event.eventLocation?.locationText && event.eventLocation.locationText !== venueTitle
      ? event.eventLocation.locationText
      : null;

  const getEventEndDate = () => {
    if (!event.endTime) return null;
    return event.endTime.toDate ? event.endTime.toDate() : new Date(event.endTime);
  };
  const eventEndDate = getEventEndDate();
  const isEventFinished = eventEndDate ? Date.now() > eventEndDate.getTime() : false;

  return (
    <div className="event-detail">
      <section className="event-hero">
        {/* Event Image with Carousel */}
        <div className="hero-left">
          <div className="image-container">
            <img
              src={event.poster?.horizontalUrl || (event.imagesUrls && event.imagesUrls[0])}
              alt="Event"
              className="event-main-image"
            />
            {/* Share Button Overlay */}
            <div className="share-overlay">
              <ShareButton eventId={eventId} eventTitle={event.name} />
            </div>
          </div>
          {/* Image Carousel Dots */}
          {event.imagesUrls && event.imagesUrls.length > 1 && (
            <div className="image-carousel-dots">
              {event.imagesUrls.map((_, index) => (
                <button
                  key={index}
                  className={`carousel-dot ${index === currentImageIndex ? 'active' : ''}`}
                  onClick={() => setCurrentImageIndex(index)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Event Details Card - Right Side */}
        <div className="hero-right">
          <h2>{event.name}</h2>

          <div className="event-info">
            <div className="info-row">
              <img src={dateicon} alt="Date" />
              <span className="info-label">Date :</span>
              <span className="info-value">{formatEventDateOnly(event.startTime)}</span>
            </div>
            <div className="info-row">
              <img src={timeicon} alt="Start Time" />
              <span className="info-label">Start Time :</span>
              <span className="info-value">{convertTo12HourFormat(event.startTime)}</span>
            </div>
            <div className="info-row">
              <img src={clockicon} alt="Gate Close" />
              <span className="info-label">Gate Close :</span>
              <span className="info-value">{convertTo12HourFormat(event.endTime)}</span>
            </div>
            <div className="info-row">
              <img src={locationicon} alt="Venue" />
              <span className="info-label">Venue :</span>
              {event.eventLocation?.googleMapLink ? (
                <a
                  href={event.eventLocation.googleMapLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="venue-link info-value"
                >
                  {venueText}
                </a>
              ) : (
                <span className="info-value">{venueText}</span>
              )}
            </div>
          </div>

          <div className="price-book">
            <span className="price">₹{formatINR(getMinimumTicketPrice())} Onwards</span>
            {isEventFinished ? (
              <button className="book-now-btn event-finished-btn" disabled>
                Event Finished
              </button>
            ) : (
              <Link to={`/select-tickets/${eventId}`}>
                <button className="book-now-btn btn-shine">Book Now</button>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Content aligned with image width */}
      <div className="event-content-wrapper">
        {/* About Event Section */}
        <Reveal as="section" className="about-event">
          <h2 className="section-title">About Event</h2>
          <div className="event-description">
            <div
              className={`description-text ${isDescriptionExpanded ? 'expanded' : 'collapsed'}`}
              dangerouslySetInnerHTML={{ __html: event.description }}
            />
            {event.description && event.description.length > 150 && (
              <button
                className="view-more"
                onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
              >
                {isDescriptionExpanded ? 'View Less' : 'View More'}
              </button>
            )}
          </div>
        </Reveal>

        {/* Artist Lineup Section — only renders when artists have preview tracks */}
        <ArtistLineup event={event} />

        {/* More Information Section */}
        <Reveal as="section" className="more-info">
          <h2 className="section-title">More Information</h2>
          <div className="info-list">
            {/* Language */}
            <div className="info-item">
              <img src={moreinfo} alt="Language" />
              <div className="info-content">
                <span className="info-label">Language</span>
                <span className="info-value">{event.metadata?.languages?.join(", ") || "English, Hindi"}</span>
              </div>
            </div>

            {/* Age Limit */}
            <div className="info-item">
              <img src={moreinfo} alt="Age Limit" />
              <div className="info-content">
                <span className="info-label">Age Limit</span>
                <span className="info-value">{event.metadata?.minAge || 18} yrs+</span>
              </div>
            </div>

            {/* Duration */}
            <div className="info-item">
              <img src={moreinfo} alt="Duration" />
              <div className="info-content">
                <span className="info-label">Duration</span>
                <span className="info-value">{calculateEventDuration(event.startTime, event.endTime)}</span>
              </div>
            </div>

            {/* Layout */}
            <div className="info-item">
              <img src={moreinfo} alt="Layout" />
              <div className="info-content">
                <span className="info-label">Layout</span>
                <span className="info-value">{event.metadata?.layout || "Indoor"}</span>
              </div>
            </div>

            {/* Parking */}
            <div className="info-item">
              <img src={moreinfo} alt="Parking" />
              <div className="info-content">
                <span className="info-label">Parking</span>
                <span className="info-value">{event.metadata?.isParkingAvailable ? "Available" : "Not Available"}</span>
              </div>
            </div>

            {/* Seating Arrangement */}
            <div className="info-item">
              <img src={moreinfo} alt="Seating Arrangement" />
              <div className="info-content">
                <span className="info-label">Seating Arrangement</span>
                <span className="info-value">Indoor</span>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Venue Section */}
        {event.eventLocation.isVenueDecided && (
          <Reveal as="section" className="venue-section">
            <h2 className="section-title">Venue</h2>
            <div className="venue-box">
              <div className="venue-left">
                <h3 className="venue-name">{venueTitle}</h3>
                {venueDescription && (
                  <p className="venue-description">{venueDescription}</p>
                )}
                <a
                  href={event.eventLocation?.googleMapLink || getDirectionsUrl(event.eventLocation?.googleMapsEmbedLink, event.eventLocation?.locationText)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="get-direction-btn"
                  style={{ color: '#FFFFFF', borderColor: '#FFFFFF' }}
                >
                  <img src={ArrowIcon} alt="" style={{ width: 16, height: 16 }} />
                  Get Direction
                </a>
              </div>
              <div className="venue-map">
                <iframe
                  src={event.eventLocation?.googleMapsEmbedLink}
                  width="100%"
                  height="300"
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Venue Map"
                ></iframe>
              </div>
            </div>
          </Reveal>
        )}

        {/* Terms & Conditions Section */}
        <Reveal as="section" className="terms-conditions">
          <button
            className="dropdown-header"
            onClick={() => setIsTermsExpanded(!isTermsExpanded)}
          >
            <h2 className="section-title">Terms & Conditions</h2>
            <span className={`dropdown-arrow ${isTermsExpanded ? 'expanded' : ''}`}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </button>
          <div className={`accordion-panel ${isTermsExpanded ? 'open' : ''}`}>
            <div className="accordion-inner">
              <div className="dropdown-content">
                <TermsAndConditions terms={event.termsAndConditions} />
              </div>
            </div>
          </div>
        </Reveal>

        {/* Frequently Asked Question Section */}
        {/* <section className="faq-section">
          <button
            className="dropdown-header"
            onClick={() => setIsFAQExpanded(!isFAQExpanded)}
          >
            <h2 className="section-title">Frequently Asked Question</h2>
            <span className={`dropdown-arrow ${isFAQExpanded ? 'expanded' : ''}`}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </button>
          {isFAQExpanded && (
            <div className="dropdown-content">
              <div className="faq-content">
                {faqData.map((faq, index) => (
                  <div key={index} className="faq-item">
                    <button
                      className={`faq-question ${expandedFAQ === index ? 'expanded' : ''}`}
                      onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                    >
                      <span>{faq.question}</span>
                      <span className="faq-arrow">{expandedFAQ === index ? '−' : '+'}</span>
                    </button>
                    {expandedFAQ === index && (
                      <div className="faq-answer">
                        <p>{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </section> */}
      </div>
      {/* End of content wrapper - sections below are full width */}

      {/* You may Also Like Section - only render when loading or when there are events */}
      {(loadingSimilarEvents || similarEvents.length > 0) && (
      <Reveal as="section" className="similar-events">
        <div className="section-header">
          <h2 className="section-title">You may Also Like</h2>
          <Link to="/events-filters">
            <button
              className="view-all-btn"
              style={{
                padding: '6px 16px',
                gap: '10px',
                alignSelf: 'stretch',
                borderRadius: '20px',
                border: '0.5px solid #FFF'
              }}
            >
              View All
            </button>
          </Link>
        </div>
        <div className="event-cards">
          {loadingSimilarEvents ? (
            <div className="loading-skeleton">
              {[1, 2, 3, 4, 5].map((index) => (
                <div key={index} className="event-card-skeleton">
                  <div className="skeleton-image"></div>
                  <div className="skeleton-text"></div>
                  <div className="skeleton-text small"></div>
                  <div className="skeleton-text small"></div>
                </div>
              ))}
            </div>
          ) : (
            similarEvents.map((similarEvent) => (
              <Link key={similarEvent.id} to={`/events/${similarEvent.id}`}>
                <div className="event-card">
                  <img
                    src={similarEvent.poster?.verticalUrl || similarEvent.poster?.horizontalUrl || event.poster?.verticalUrl}
                    alt={similarEvent.name}
                  />
                  <h3>{similarEvent.name}</h3>
                  <p>{formatEventDateOnly(similarEvent.startTime)} | {convertTo12HourFormat(similarEvent.startTime)}</p>
                  <p>{similarEvent.eventLocation?.locationText}</p>
                </div>
              </Link>
            ))
          )}
        </div>
      </Reveal>
      )}

      <AppDownloadModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export default EventDetails;